// Stato dell'asta offline come reducer puro.
//
// Prima viveva in una decina di useState dentro un componente da 430 righe, con
// tre punti che mutavano lo stato sul posto (copia superficiale + push
// sull'oggetto annidato): sotto StrictMode di React 19 un updater invocato due
// volte poteva aggiungere il giocatore due volte e addebitare il prezzo due
// volte.

import {
    DEFAULT_BUDGET,
    DEFAULT_PLAYERS_PER_TEAM,
    ROLES,
} from './constants';
import { isRoleFull, maxBid, ownsPlayer, refundAmount } from './team';

export const STORAGE_KEY = 'astafanta:offline';
export const STATE_VERSION = 1;

export function initialState() {
    const participants = Array.from({ length: 4 }, (_, i) => `Partecipante ${i + 1}`);
    const teams = {};
    participants.forEach((name) => {
        teams[name] = { budget: DEFAULT_BUDGET.live, roster: [] };
    });
    return {
        version: STATE_VERSION,
        participants,
        teams,
        settings: {
            budget: DEFAULT_BUDGET.live,
            players_per_team: { ...DEFAULT_PLAYERS_PER_TEAM },
        },
    };
}

function withTeams(state, teams) {
    return { ...state, teams };
}

function replaceTeam(state, participant, team) {
    return withTeams(state, { ...state.teams, [participant]: team });
}

export class OfflineRuleError extends Error {}

function reject(message) {
    throw new OfflineRuleError(message);
}

export function offlineReducer(state, action) {
    switch (action.type) {
        case 'reset':
            return initialState();

        case 'set_settings': {
            const settings = { ...state.settings, ...action.settings };
            // Il budget cambia solo per chi non ha ancora speso nulla.
            const teams = {};
            Object.entries(state.teams).forEach(([name, team]) => {
                teams[name] = team.roster.length
                    ? team
                    : { ...team, budget: settings.budget };
            });
            return { ...state, settings, teams };
        }

        case 'add_participant': {
            const name = action.name.trim();
            if (!name) reject('Il nome non puo essere vuoto.');
            if (state.participants.includes(name)) reject('Nome gia usato.');
            return {
                ...state,
                participants: [...state.participants, name],
                teams: {
                    ...state.teams,
                    [name]: { budget: state.settings.budget, roster: [] },
                },
            };
        }

        case 'rename_participant': {
            const { oldName } = action;
            const newName = action.newName.trim();
            if (!newName || newName === oldName) return state;
            if (state.participants.includes(newName)) reject('Nome gia usato.');
            // Prima la firma prevedeva un terzo argomento `index` che il
            // chiamante non passava: participants conservava il vecchio nome
            // mentre teams veniva ri-chiavato sul nuovo, e la squadra spariva.
            const teams = { ...state.teams };
            teams[newName] = teams[oldName];
            delete teams[oldName];
            return {
                ...state,
                participants: state.participants.map((p) => (p === oldName ? newName : p)),
                teams,
            };
        }

        case 'remove_participant': {
            const teams = { ...state.teams };
            delete teams[action.name];
            return {
                ...state,
                participants: state.participants.filter((p) => p !== action.name),
                teams,
            };
        }

        case 'assign_player': {
            const { participant, player, price } = action;
            const team = state.teams[participant];
            if (!team) reject('Partecipante non trovato.');
            if (!Number.isInteger(price) || price < 1) {
                reject('Il prezzo deve essere un numero intero maggiore di zero.');
            }
            if (Object.values(state.teams).some((t) => ownsPlayer(t, player.ID))) {
                reject('Questo giocatore e gia stato assegnato.');
            }
            if (isRoleFull(team, player.Ruolo, state.settings.players_per_team)) {
                reject(`Ruolo ${player.Ruolo} gia al completo per ${participant}.`);
            }
            const ceiling = maxBid(team, state.settings.players_per_team);
            if (price > ceiling) {
                // Parita con il server: offline si poteva arrivare a zero
                // crediti con quindici slot ancora vuoti.
                reject(
                    `Puoi spendere al massimo ${ceiling}: serve un credito per ogni altro slot da riempire.`,
                );
            }
            return replaceTeam(state, participant, {
                ...team,
                budget: team.budget - price,
                roster: [
                    ...team.roster,
                    { player_id: player.ID, role: player.Ruolo, purchase_price: price },
                ],
            });
        }

        case 'remove_player': {
            const { participant, playerId } = action;
            const team = state.teams[participant];
            if (!team) return state;
            const entry = team.roster.find((r) => r.player_id === playerId);
            if (!entry) return state;
            return replaceTeam(state, participant, {
                ...team,
                budget: team.budget + entry.purchase_price,
                roster: team.roster.filter((r) => r.player_id !== playerId),
            });
        }

        case 'release_player': {
            const { participant, playerId, inSerieA } = action;
            const team = state.teams[participant];
            if (!team) return state;
            const entry = team.roster.find((r) => r.player_id === playerId);
            if (!entry) return state;
            return replaceTeam(state, participant, {
                ...team,
                budget: team.budget + refundAmount(entry.purchase_price, inSerieA),
                roster: team.roster.filter((r) => r.player_id !== playerId),
            });
        }

        case 'import_teams': {
            const teams = {};
            const participants = [];
            Object.entries(action.teams).forEach(([name, imported]) => {
                participants.push(name);
                const spent = imported.roster.reduce(
                    (total, entry) => total + entry.purchase_price,
                    0,
                );
                teams[name] = {
                    budget: state.settings.budget - spent,
                    roster: imported.roster.filter((entry) => ROLES.includes(entry.role)),
                    imported_name: imported.imported_name,
                };
            });
            if (!participants.length) reject('Il file non contiene squadre leggibili.');
            return { ...state, participants, teams };
        }

        default:
            return state;
    }
}
